import { useEffect, useState } from "react";
import { Star, BadgeCheck } from "lucide-react";
import { http } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";

export const Stars = ({ value, size = 16 }) => (
  <span className="inline-flex gap-0.5" aria-label={`${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} className={i <= value ? "fill-[#E0A030] text-[#E0A030]" : "text-[#D9CFC3]"} />
    ))}
  </span>
);

const ReviewCard = ({ r }) => (
  <div data-testid={`review-card-${r.id}`} className="flex h-full flex-col overflow-hidden rounded-[24px] border border-[#E3D9CD] bg-white">
    {r.photo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${r.photo_url}`} alt={`Photo from ${r.name}`} className="aspect-[4/3] w-full object-cover" />}
    <div className="flex flex-1 flex-col p-6">
      <Stars value={r.rating} />
      <h3 className="font-display mt-4 text-xl">{r.title}</h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-[#4A4543]">{r.body}</p>
      <p className="mt-5 flex items-center gap-1.5 text-xs text-[#6E6661]">
        {r.name}
        {r.verified && <><BadgeCheck size={14} className="text-[#5E7153]" /> Verified buyer</>}
      </p>
    </div>
  </div>
);

export const Reviews = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    http.get("/reviews").then((res) => setData(res.data)).catch(() => setData(null));
  }, []);
  const items = Array.isArray(data?.reviews) ? data.reviews : [];
  return (
    <section id="reviews" data-testid="reviews-section" className="bg-[#FDFBF7] py-20 sm:py-28">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal className="mb-12 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <p className="eyebrow mb-4 text-[#C85A32]">From our customers</p>
            <h2 className="font-display text-4xl font-light tracking-tight sm:text-5xl">Real pours, <span className="italic">real reviews.</span></h2>
          </div>
          {items.length > 0 && (
            <p data-testid="reviews-average" className="flex items-center gap-3 text-sm text-[#4A4543]">
              <Stars value={Math.round(data?.average ?? 0)} /> {data?.average} average · {items.length} verified review{items.length === 1 ? "" : "s"}
            </p>
          )}
        </Reveal>
        {items.length === 0 ? (
          <Reveal>
            <div data-testid="reviews-empty" className="rounded-[28px] border border-dashed border-[#D9CFC3] bg-[#F2EAE1]/50 p-10 sm:p-14">
              <Stars value={0} size={20} />
              <p className="font-display mt-5 text-3xl font-light">Be among the first to review.</p>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#4A4543]">We only publish reviews from verified buyers. After your Slowpour arrives, we'll email you a link to share your honest thoughts — and a photo of your favourite pour.</p>
            </div>
          </Reveal>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((r, i) => <Reveal key={r.id} delay={(i % 3) * 0.06}><ReviewCard r={r} /></Reveal>)}
          </div>
        )}
      </div>
    </section>
  );
};
